/**
 * Loading + empty + error state primitives, reused across pages/cards.
 */
import { useTranslation } from "react-i18next";

interface SkeletonProps {
  className?: string;
}

/** A single shimmering placeholder block. */
export function Skeleton({ className = "" }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse rounded-md bg-bg-subtle ${className}`}
      aria-hidden="true"
    />
  );
}

/** Card-shaped skeleton for KPI tiles. */
export function CardSkeleton() {
  return (
    <div className="rounded-card border border-border bg-bg-elevated p-5">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="mt-4 h-8 w-32" />
    </div>
  );
}

/** Full-height skeleton for a chart panel. */
export function ChartSkeleton({
  height = 288,
}: {
  height?: number | string;
}) {
  return (
    <div
      role="status"
      aria-busy="true"
      className="w-full animate-pulse rounded-md bg-bg-subtle"
      style={{ height }}
    />
  );
}

export function EmptyState({ hint }: { hint?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-12 text-center">
      <p className="text-sm font-medium text-fg-muted">{t("state.empty")}</p>
      <p className="text-xs text-fg-subtle">{hint ?? t("state.emptyHint")}</p>
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-2 py-12 text-center"
    >
      <p className="text-sm font-medium text-status-critical">{t("state.error")}</p>
      <p className="text-xs text-fg-subtle">{t("state.errorHint")}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg hover:bg-bg-subtle"
        >
          {t("actions.retry")}
        </button>
      )}
    </div>
  );
}
